import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import './Notifications.css';

interface Notification {
  id: number;
  titulo: string;
  mensagem: string;
  tipo: string;
  data_envio: string;
  data_leitura: string | null;
  lida: boolean;
  link: string | null;
}

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  // =============================================
  // CARREGAR NOTIFICAÇÕES
  // =============================================

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchNotifications = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getNotifications({
        limit: 100,
        unreadOnly: filter === 'unread'
      });

      if (response.success && response.data) {
        setNotifications(response.data);
      }
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // =============================================
  // MARCAR COMO LIDA
  // =============================================

  const markAsRead = async (id: number) => {
    try {
      await apiService.markNotificationAsRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, lida: true, data_leitura: new Date().toISOString() } : n)
      );
    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
    }
  };

  // =============================================
  // MARCAR TODAS COMO LIDAS
  // =============================================

  const markAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      setNotifications(prev =>
        prev.map(n => ({ ...n, lida: true, data_leitura: new Date().toISOString() }))
      );
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
    }
  };

  // =============================================
  // DELETAR NOTIFICAÇÃO
  // =============================================

  const deleteNotification = async (id: number) => {
    if (!window.confirm('Deseja realmente deletar esta notificação?')) {
      return;
    }

    try {
      await apiService.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      console.error('Erro ao deletar notificação:', error);
    }
  };

  // =============================================
  // DELETAR TODAS LIDAS
  // =============================================

  const deleteAllRead = async () => {
    if (!window.confirm('Deseja realmente deletar todas as notificações lidas?')) {
      return;
    }

    try {
      // Deletar individualmente cada notificação lida
      const readNotifications = notifications.filter(n => n.lida);
      for (const notification of readNotifications) {
        await apiService.deleteNotification(notification.id);
      }
      setNotifications(prev => prev.filter(n => !n.lida));
    } catch (error) {
      console.error('Erro ao deletar notificações:', error);
    }
  };

  // =============================================
  // HANDLE CLICK
  // =============================================

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.lida) {
      markAsRead(notification.id);
    }

    if (notification.link) {
      navigate(notification.link);
    }
  };

  // =============================================
  // MAPEAR TIPO
  // =============================================

  const mapTypeToDisplay = (tipo: string): { icon: string; class: string; label: string } => {
    const map: Record<string, { icon: string; class: string; label: string }> = {
      'success': { icon: 'fa-check-circle', class: 'success', label: 'Sucesso' },
      'error': { icon: 'fa-times-circle', class: 'error', label: 'Erro' },
      'warning': { icon: 'fa-exclamation-triangle', class: 'warning', label: 'Aviso' },
      'info': { icon: 'fa-info-circle', class: 'info', label: 'Informação' },
      'execution': { icon: 'fa-play-circle', class: 'info', label: 'Execução' },
      'system': { icon: 'fa-cog', class: 'info', label: 'Sistema' },
      'queue': { icon: 'fa-list', class: 'warning', label: 'Fila' },
      'user': { icon: 'fa-user', class: 'info', label: 'Usuário' },
      'performance': { icon: 'fa-chart-line', class: 'warning', label: 'Performance' }
    };

    return map[tipo] || map['info'];
  };

  // =============================================
  // FORMAT DATE
  // =============================================

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMs / 3600000);
    const diffInDays = Math.floor(diffInMs / 86400000);

    if (diffInMinutes < 1) return 'Agora';
    if (diffInMinutes < 60) return `${diffInMinutes}min atrás`;
    if (diffInHours < 24) return `${diffInHours}h atrás`;
    if (diffInDays < 7) return `${diffInDays}d atrás`;

    return date.toLocaleDateString('pt-BR');
  };

  // =============================================
  // STATS
  // =============================================

  const unreadCount = notifications.filter(n => !n.lida).length;
  const readCount = notifications.filter(n => n.lida).length;

  // =============================================
  // RENDER
  // =============================================

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <div className="notifications-header-left">
          <h1>
            <i className="fas fa-bell"></i> Notificações
          </h1>
          <div className="notifications-stats">
            <span className="stat">
              <i className="fas fa-envelope"></i> {unreadCount} não lidas
            </span>
            <span className="stat">
              <i className="fas fa-envelope-open"></i> {readCount} lidas
            </span>
          </div>
        </div>

        <div className="notifications-header-actions">
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              <i className="fas fa-list"></i> Todas
            </button>
            <button
              className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
              onClick={() => setFilter('unread')}
            >
              <i className="fas fa-envelope"></i> Não lidas
            </button>
          </div>

          {unreadCount > 0 && (
            <button className="action-btn" onClick={markAllAsRead}>
              <i className="fas fa-check-double"></i> Marcar todas como lidas
            </button>
          )}

          {readCount > 0 && (
            <button className="action-btn danger" onClick={deleteAllRead}>
              <i className="fas fa-trash"></i> Limpar lidas
            </button>
          )}
        </div>
      </div>

      <div className="notifications-content">
        {isLoading ? (
          <div className="loading">
            <i className="fas fa-spinner fa-spin"></i> Carregando notificações...
          </div>
        ) : notifications.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-bell-slash"></i>
            <h3>Nenhuma notificação</h3>
            <p>
              {filter === 'unread'
                ? 'Você não tem notificações não lidas'
                : 'Você ainda não recebeu nenhuma notificação'}
            </p>
          </div>
        ) : (
          <div className="notifications-list">
            {notifications.map(notification => {
              const typeInfo = mapTypeToDisplay(notification.tipo);

              return (
                <div
                  key={notification.id}
                  className={`notification-card ${!notification.lida ? 'unread' : ''} ${typeInfo.class} ${notification.link ? 'clickable' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    <i className={`fas ${typeInfo.icon}`}></i>
                  </div>

                  <div className="notification-body">
                    <div className="notification-header-card">
                      <h3>{notification.titulo}</h3>
                      <div className="notification-meta">
                        <span className="notification-type">
                          <i className={`fas ${typeInfo.icon}`}></i> {typeInfo.label}
                        </span>
                        <span className="notification-time">
                          <i className="fas fa-clock"></i> {formatDate(notification.data_envio)}
                        </span>
                      </div>
                    </div>

                    <p className="notification-message">{notification.mensagem}</p>

                    {notification.link && (
                      <div className="notification-link-indicator">
                        <i className="fas fa-external-link-alt"></i> Clique para acessar
                      </div>
                    )}
                  </div>

                  <div className="notification-actions">
                    {!notification.lida && (
                      <button
                        className="action-icon-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notification.id);
                        }}
                        title="Marcar como lida"
                      >
                        <i className="fas fa-check"></i>
                      </button>
                    )}

                    <button
                      className="action-icon-btn danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification.id);
                      }}
                      title="Deletar"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>

                  {!notification.lida && <div className="unread-indicator"></div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
